/**
 * Authentication Context Provider
 * 
 * Authentication context provider for WMS DAOUXUN with:
 * - User authentication state management
 * - Login/logout functionality
 * - Token management
 * - Permission management
 * - Session persistence
 * - Auto-login on page load
 * 
 * Uses reducer pattern for state management and localStorage for persistence.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} The authentication provider component
 */
import React, { useReducer, useEffect, useMemo, useCallback } from 'react';
import { api, tokenUtils, handleApiError } from '../services/api.js';
import { API_ENDPOINTS } from '../config/api.config.js';
import { StorageKeys, initialState, AUTH_ACTIONS, authReducer, AuthContext } from './AuthContext.helpers.js';
import { getDefaultDashboard, hasRole, hasPermission } from '../utils/userRoles.js';
import { createLogger } from '../utils/logger';

const logger = createLogger('auth');

// Provider
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check authentication on load (simplified version)
  useEffect(() => {
    const init = async () => {
      const token = localStorage.getItem(StorageKeys.JWT_TOKEN_KEY);
      const userDataStr = localStorage.getItem(StorageKeys.JWT_USER_KEY);

      if (token) {
        if (userDataStr) {
          try {
            const user = JSON.parse(userDataStr);
            if (user.role && !user.permissions) user.permissions = [user.role];
            dispatch({
              type: AUTH_ACTIONS.LOGIN_SUCCESS,
              payload: {
                user,
                permissions: user.permissions || (user.role ? [user.role] : [])
              }
            });
          } catch {
            localStorage.removeItem(StorageKeys.JWT_USER_KEY);
          }
        } else {
          try {
            const resp = await api.get(API_ENDPOINTS.PROFILE);
            const user = resp.data || null;
            if (user) {
              if (user.role && !user.permissions) user.permissions = [user.role];
              localStorage.setItem(StorageKeys.JWT_USER_KEY, JSON.stringify(user));
              dispatch({
                type: AUTH_ACTIONS.LOGIN_SUCCESS,
                payload: {
                  user,
                  permissions: user.permissions || (user.role ? [user.role] : [])
                }
              });
            }
          } catch {
            tokenUtils.clearTokens();
            localStorage.removeItem(StorageKeys.JWT_USER_KEY);
          }
        }
      }
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    };
    init();
  }, []);

  // Login (memoized)
  const login = useCallback(async (credentials) => {
    logger.debug('AuthContext: Starting login', credentials);
    
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      const loginPayload = {
        username: credentials.username,
        password: credentials.password
      };

      logger.debug('AuthContext: API login call...');
      const response = await api.post(API_ENDPOINTS.LOGIN, loginPayload);
      logger.debug('AuthContext: API response received', response.data)

      const data = response.data || {};
      const access_token = data.access_token || '';
      const refresh_token = data.refresh_token || response.headers?.['x-refresh-token'] || '';

      const userData = data.user || data.user_info || data.userData || null;
      if (!userData) {
        throw new Error('Invalid login response: missing user data');
      }

      if (userData.role && !userData.permissions) {
        userData.permissions = [userData.role];
      }

      tokenUtils.setTokens(access_token, refresh_token);
      logger.debug('AuthContext: setTokens called with:', access_token, refresh_token);
      logger.debug('AuthContext: localStorage after setTokens:', {
        access_token: localStorage.getItem(StorageKeys.JWT_TOKEN_KEY),
        refresh_token: localStorage.getItem(StorageKeys.JWT_REFRESH_KEY),
        wms_user: localStorage.getItem(StorageKeys.JWT_USER_KEY)
      });

      localStorage.setItem(StorageKeys.JWT_USER_KEY, JSON.stringify(userData));
      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: {
          user: userData,
          permissions: userData.permissions || (userData.role ? [userData.role] : [])
        }
      });

      const redirectTo = getDefaultDashboard(userData);
      logger.debug('AuthContext: Login successful, redirecting to:', redirectTo);

      return {
        success: true,
        user: userData,
        redirectTo
      };

    } catch (error) {
      const apiError = handleApiError(error);
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: apiError.message
      });

      return {
        success: false,
        error: apiError.message
      };
    }
  }, []);

  // Logout (memoized)
  const logout = useCallback(async () => {
    try {
      // Notify server (optional, don't block if it fails)
      try {
        await api.post(API_ENDPOINTS.LOGOUT);
      } catch (error) { console.error(error); }
    } finally {
      // Clear local data
      tokenUtils.clearTokens();
      localStorage.removeItem(StorageKeys.JWT_USER_KEY);
      
      // Also clear session recovery data that may interfere
      localStorage.removeItem('session_active');
      localStorage.removeItem('session_last_activity');
      localStorage.removeItem('session_metadata');
      
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
  }, []);

  // Check authentication status (manual, memoized)
  const checkAuthStatus = useCallback(async () => {
    try {
      const token = tokenUtils.getToken();
      
      if (!token || tokenUtils.isTokenExpired(token)) {
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
        return false;
      }

      // Verify with server
      const response = await api.get(API_ENDPOINTS.PROFILE);
      const user = response.data;

      if (user.role && !user.permissions) {
        user.permissions = [user.role];
      }

      // Update user data
      localStorage.setItem(StorageKeys.JWT_USER_KEY, JSON.stringify(user));
      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: {
          user,
          permissions: user.permissions || (user.role ? [user.role] : [])
        }
      });

      return true;

    } catch (error) {
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      tokenUtils.clearTokens();
      localStorage.removeItem(StorageKeys.JWT_USER_KEY);
      return false;
    }
  }, []);

  // Update profile (memoized)
  const updateProfile = useCallback(async (profileData) => {
    try {
      const response = await api.put(API_ENDPOINTS.PROFILE, profileData);
      const updatedUser = response.data;

      localStorage.setItem(StorageKeys.JWT_USER_KEY, JSON.stringify(updatedUser));
      dispatch({
        type: AUTH_ACTIONS.UPDATE_USER,
        payload: updatedUser
      });

      return { success: true, user: updatedUser };
    } catch (error) {
      const apiError = handleApiError(error);
      return { success: false, error: apiError.message };
    }
  }, []);

  // Change password (memoized)
  const changePassword = useCallback(async (passwordData) => {
    try {
      await api.post(`${API_ENDPOINTS.AUTH}/change-password`, passwordData);
      return { success: true };
    } catch (error) {
      const apiError = handleApiError(error);
      return { success: false, error: apiError.message };
    }
  }, []);

  // Authorization checks (memoized)
  const checkRole = useCallback((role) => hasRole(state.user, role), [state.user]);
  const checkPermission = useCallback((permission) => hasPermission(state.user, permission), [state.user]);
  const canAccess = useCallback((requiredRoles = [], requiredPermissions = []) => {
    if (!state.isAuthenticated) return false;
    
    // Check roles
    if (requiredRoles.length > 0) {
      const hasRequiredRole = requiredRoles.some(role => checkRole(role));
      if (!hasRequiredRole) return false;
    }

    // Check permissions
    if (requiredPermissions.length > 0) {
      const hasRequiredPermission = requiredPermissions.some(permission => 
        checkPermission(permission)
      );
      if (!hasRequiredPermission) return false;
    }

    return true;
  }, [state.isAuthenticated, state.user, checkRole, checkPermission]);

  // Clear errors (memoized)
  const clearError = useCallback(() => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  }, []);

  // Context value (memoized to prevent unnecessary re-renders)
  const contextValue = useMemo(() => ({
    // State
    ...state,
    
    // Token accessor for components that need the raw token
    token: localStorage.getItem(StorageKeys.JWT_TOKEN_KEY),
    
    // Actions
    login,
    logout,
    updateProfile,
    changePassword,
    checkAuthStatus,
    clearError,
    
    // Checks
    checkRole,
    checkPermission,
    canAccess,
    
    // Utilities
    getDefaultDashboard: () => getDefaultDashboard(state.user),
    isAdmin: () => checkRole('admin'),
    isManager: () => checkRole('manager'),
    isOperator: () => checkRole('operator'),
    isClient: () => checkRole('client'),
    isSelfService: () => checkRole('selfservice'),
    isWarehouse: () => checkRole('warehouse')
  }), [
    state,
    login,
    logout,
    updateProfile,
    changePassword,
    checkAuthStatus,
    clearError,
    checkRole,
    checkPermission,
    canAccess
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};


export default AuthProvider;

